import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { IActivity, KeyStage } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { SkiaCanvas } from './SkiaCanvas';
import { ActivityMediaRenderer } from './ActivityMediaRenderer';
import { useStageTokens } from '../hooks/useStageTokens';

interface ComprehensionActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

/**
 * ComprehensionActivity — media-first canvas response.
 *
 * Layout:
 *   1. Supporting media (image, audio clip etc.) shown BEFORE the canvas.
 *   2. The response prompt (from config.prompt) in a callout box.
 *   3. Skia handwriting canvas for the student's written answer.
 *   4. Clear / Submit footer.
 *
 * Teacher feedback overlay sits on absoluteFill above everything.
 */
export const ComprehensionActivity: React.FC<ComprehensionActivityProps> = ({ activity, onComplete, keyStage }) => {
    const prompt: string = activity.config?.prompt || 'Write your response below.';
    const media = activity.media || [];
    const tokens = useStageTokens(keyStage);

    const { completedStrokes, clearStrokes } = useStrokeStore();

    return (
        <View style={styles.container}>
            {/* Scrollable content area (media + prompt) */}
            <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.headerRow}>
                        <Text style={[styles.title, { fontSize: tokens.titleFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.title}</Text>
                    </View>
                    {activity.instructions && (
                        <Text style={[styles.instructions, { fontSize: tokens.bodyFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.instructions}</Text>
                    )}
                </View>

                {/* Supporting media — before placement */}
                <ActivityMediaRenderer media={media} placement="before" />

                {/* Response prompt callout */}
                <View style={styles.promptBox}>
                    <Text style={styles.promptLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✏️ Your task:</Text>
                    <Text style={[styles.promptText, { fontSize: tokens.bodyFontSize, lineHeight: tokens.bodyLineHeight }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{prompt}</Text>
                </View>

                {/* Alongside media (e.g. audio hint while writing) */}
                <ActivityMediaRenderer media={media} placement="alongside" />
            </ScrollView>

            {/* Canvas area */}
            <View testID="comprehension-canvas-area" style={styles.canvasArea}
                accessibilityRole="image"
                accessibilityLabel="Handwriting canvas for writing your response"
            >
                <SkiaCanvas />
            </View>

            {/* Footer controls */}
            <View style={styles.footer}>
                <TouchableOpacity style={styles.clearBtn} onPress={clearStrokes}
                    accessibilityRole="button"
                    accessibilityLabel="Clear"
                    accessibilityHint="Clears all strokes from the canvas"
                >
                    <Text style={styles.clearTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Clear</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.submitBtn, completedStrokes.length === 0 && styles.disabledBtn]}
                    onPress={onComplete}
                    disabled={completedStrokes.length === 0}
                    accessibilityRole="button"
                    accessibilityLabel="Done"
                    accessibilityHint="Submits your response"
                    accessibilityState={{ disabled: completedStrokes.length === 0 }}
                >
                    <Text style={styles.submitTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>DONE! ➔</Text>
                </TouchableOpacity>
            </View>

            {/* Teacher feedback overlay — always present, read-only */}
            <View style={StyleSheet.absoluteFill} pointerEvents="none">
                <SkiaCanvas readOnly={true} />
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: '#FFFFFF', borderRadius: 16 },

    scrollArea: { flex: 0, maxHeight: '45%' },
    scrollContent: { padding: 20, gap: 12 },

    header: { marginBottom: 4 },
    headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 },
    title: { fontSize: 22, fontWeight: 'bold', color: '#1E293B', marginBottom: 4 },
    instructions: { fontSize: 15, color: '#64748B', fontStyle: 'italic' },

    promptBox: {
        backgroundColor: '#EEF2FF', borderRadius: 12,
        borderLeftWidth: 4, borderLeftColor: '#6366F1',
        padding: 14,
    },
    promptLabel: { fontSize: 12, fontWeight: 'bold', color: '#6366F1', marginBottom: 4, letterSpacing: 0.5 },
    promptText: { fontSize: 16, color: '#1E293B', lineHeight: 24 },

    canvasArea: { flex: 1, marginHorizontal: 16, marginBottom: 8, borderRadius: 12, overflow: 'hidden', backgroundColor: '#FAFAFA' },

    footer: {
        flexDirection: 'row', paddingHorizontal: 16, paddingVertical: 10, gap: 10,
        borderTopWidth: 1, borderTopColor: '#F1F5F9',
    },
    clearBtn: {
        flex: 1, height: 56, minHeight: 52, borderRadius: 12, borderWidth: 1, borderColor: '#CBD5E1',
        alignItems: 'center', justifyContent: 'center',
    },
    clearTxt: { fontSize: 15, fontWeight: '600', color: '#64748B' },
    submitBtn: {
        flex: 3,
        height: 56,
        minHeight: 52,
        backgroundColor: '#22c55e',
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 4,
    },
    disabledBtn: { backgroundColor: '#D1D5DB' },
    submitTxt: { fontSize: 18, fontWeight: '900', color: '#fff', letterSpacing: 0.5 },
});
